import dayjs from "dayjs";
import { useHasPermission } from "../auth/useClaims";

// Shared vocabulary for the Alarm Response screens. Incident types and causes
// come from GET /alarms/lookups at runtime; these fallbacks only cover the
// moment before that answers, so a label never flashes as a raw code.
export const INCIDENT_LABEL = {
  ALARM: "Alarm",
  INTRUSION: "Intrusion",
  PANIC: "Panic",
  FIRE: "Fire",
  TAMPER: "Tamper",
  POWER_BATTERY: "Power / Battery",
  OTHER: "Other",
};

// What set the alarm off (Batch180). The mobile unit picks it when reporting; the control
// room can change it - one field for both. THEFT_ATTEMPT is the "Theft" code. Same codes and
// labels as the API (AlarmCodes.Causes); kept here so filters work whatever the API version.
export const CAUSE_LABEL = {
  THEFT_ATTEMPT: "Theft",
  FALSE_ALARM: "False alarm",
  MANUAL_ALARM: "Manual alarm",
  UNKNOWN: "Unknown",
  OTHERS: "Others",
};

export const CAUSE_OPTIONS = Object.entries(CAUSE_LABEL).map(([code, label]) => ({ code, label }));

// "Others" needs a description.
export const CAUSE_NEEDING_REMARKS = "OTHERS";

export const CAUSE_STYLE = {
  THEFT_ATTEMPT: { color: "#fff", background: "#C0392B" },
  FALSE_ALARM: { color: "#fff", background: "#1E7B4A" },
  MANUAL_ALARM: { color: "#fff", background: "#0D6EFD" },
  UNKNOWN: { color: "#fff", background: "#6C757D" },
  OTHERS: { color: "#fff", background: "#6F42C1" },
};

// Order matters: it is the order the board groups by.
export const ALARM_STATUSES = ["DISPATCHED", "OPENED", "ARRIVED", "REPORTED", "CLOSED", "CONVERTED", "CANCELLED"];

export const ALARM_STATUS_LABEL = {
  DISPATCHED: "Dispatched",
  OPENED: "On the way",
  ARRIVED: "On site",
  REPORTED: "Reported back",
  CLOSED: "Closed",
  CONVERTED: "Pushed to Tech",
  CANCELLED: "Cancelled",
};

export const ALARM_STATUS_CLASS = {
  DISPATCHED: "badge-linedanger",
  OPENED: "badge-lineinfo",
  ARRIVED: "badge-lineinfo",
  REPORTED: "badge-linewarning",
  CLOSED: "badge-linesuccess",
  CONVERTED: "badge-linesuccess",
  CANCELLED: "badge-linesecondary",
};

export const ACTIVE_STATUSES = ["DISPATCHED", "OPENED", "ARRIVED", "REPORTED"];

export const useCanDispatchAlarms = () => useHasPermission("Alarms.Dispatch");
export const useCanManageMobileUnits = () => useHasPermission("Alarms.ManageUnits");

export const formatDateTime = (iso) => (iso ? dayjs(iso).format("DD MMM YYYY, h:mm A") : "—");
export const formatTime = (iso) => (iso ? dayjs(iso).format("h:mm A") : "—");

// Seconds -> "45s", "12m 05s", "1h 20m". Null stays a dash: the step has not happened.
export const formatDuration = (seconds) => {
  if (seconds == null) return "—";
  const s = Math.max(0, Math.round(seconds));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
  return `${Math.floor(m / 60)}h ${String(m % 60).padStart(2, "0")}m`;
};

export const formatDistance = (meters) => {
  if (meters == null) return "—";
  return meters >= 1000 ? `${(meters / 1000).toFixed(2)} km` : `${Math.round(meters)} m`;
};

// Arrival further than this from the customer's saved pin is flagged: either the
// pin is wrong or the unit was not where it said.
export const ARRIVAL_WARN_METERS = 300;

// A position reported less accurately than this is not a GPS fix (a desktop browser
// estimates from the network - the first live test read ±2,046 m), so distances built
// on it prove nothing. The step is still recorded; it is just flagged.
export const LOW_GPS_ACCURACY_METERS = 150;
export const isLowAccuracy = (meters) => meters != null && meters > LOW_GPS_ACCURACY_METERS;
export const formatAccuracy = (meters) => (meters == null ? "" : `±${Math.round(meters)} m`);

export const mapsLink = (lat, lng) =>
  lat != null && lng != null ? `https://www.google.com/maps/search/?api=1&query=${lat},${lng}` : null;

export const mapsAddressLink = (address) =>
  address ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(address)}` : null;

// Live "time since" for an open alarm, from when it was dispatched.
export const sinceDispatched = (iso, now = Date.now()) =>
  iso ? formatDuration((now - new Date(iso).getTime()) / 1000) : "—";
